import React, { useState, useCallback, useEffect } from 'react';
import { Modal, Box, FormControl, FormLabel, RadioGroup, FormControlLabel, Radio, Button, IconButton, Chip, Checkbox, Tabs, Tab } from '@mui/material';
import { styled } from "@mui/material/styles";
import { useNavigate } from 'react-router-dom';
import CustomMonthDatePicker from '../../../Widgets/CustomDates/CustomMonthDatePicker/CustomMonthDatePicker';
import CustomYearDatePicker from '../../../Widgets/CustomDates/CustomYearDatePicker/CustomYearDatePicker';
import AutocompleteSelect from '../../../Widgets/AutocompleteSelect/AutocompleteSelect';
import DownloadIcon from '@mui/icons-material/Download';
import CloseIcon from '@mui/icons-material/Close';
import VisibilityIcon from '@mui/icons-material/Visibility';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import ClearAllIcon from '@mui/icons-material/ClearAll';
import EnumsService from '../../../../Services/enums.service.js';
import ReportsService from '../../../../Services/reports.service.js';
import WithholdingUnpaidPaymentsTab from './WithholdingUnpaidPaymentsTab.jsx';

// Dev pattern wrapper styles
const devPatternStyles = `
    .dev-pattern-wrapper {
        border: 2px solid #0066cc;
        border-radius: 8px;
        padding: 10px;
        margin: 5px;
    }
    
    @keyframes pulse {
        0%, 100% {
            transform: scale(1);
            opacity: 1;
        }
        50% {
            transform: scale(1.05);
            opacity: 0.8;
        }
    }
    
    @keyframes float {
        0%, 100% {
            transform: translateY(0px);
        }
        50% {
            transform: translateY(-10px);
        }
    }
    
    @keyframes bounce {
        0%, 100% {
            transform: translateX(-50%) translateY(0px);
        }
        50% {
            transform: translateX(-50%) translateY(-8px);
        }
    }
`;

// Modal style matching TrialBalanceReportHtml
const modalStyle = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: '73%',
    height: '90%',
    bgcolor: 'background.paper',
    boxShadow: 24,
    borderRadius: '10px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    overflow: 'hidden',
};

// Sleek styled button component
const SleekButton = styled(Button)(({ theme }) => ({
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    borderRadius: '6px',
    padding: '6px 12px',
    fontSize: '11px',
    fontWeight: '600',
    textTransform: 'none',
    boxShadow: '0 2px 6px rgba(102, 126, 234, 0.25)',
    border: 'none',
    color: '#ffffff',
    minWidth: 'auto',
    height: '32px',
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    '&:hover': {
        background: 'linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%)',
        transform: 'translateY(-1px)',
        boxShadow: '0 3px 8px rgba(102, 126, 234, 0.35)',
    },
    '&:active': {
        transform: 'translateY(0px)',
        boxShadow: '0 2px 6px rgba(102, 126, 234, 0.25)',
    },
    '& .MuiButton-startIcon': {
        marginRight: '4px',
        marginLeft: '0px',
        '& svg': {
            fontSize: '14px',
        }
    }
}));

// Sleek styled radio group
const StyledRadioGroup = styled(RadioGroup)(({ theme }) => ({
    display: 'flex',
    flexDirection: 'row',
    gap: '4px',
    marginTop: '0px',
    '& .MuiFormControlLabel-root': {
        margin: 0,
        padding: '4px 10px',
        borderRadius: '4px',
        backgroundColor: '#ffffff',
        border: '1px solid #e3e8ff',
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        minHeight: '28px',
        '&:hover': {
            backgroundColor: '#f0f2ff',
            transform: 'translateY(-1px)',
            boxShadow: '0 2px 6px rgba(102, 126, 234, 0.15)',
        },
        // Target when the FormControlLabel contains a checked radio
        '&.Mui-checked': {
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%) !important',
            color: '#ffffff !important',
            border: '1px solid #667eea !important',
            '& .MuiFormControlLabel-label': {
                color: '#ffffff !important',
            }
        }
    },
    '& .MuiFormControlLabel-label': {
        fontSize: '12px',
        fontWeight: '600',
        color: '#2c3e50',
        marginLeft: '2px',
        whiteSpace: 'nowrap',
    },
    '& .MuiRadio-root': {
        color: '#667eea',
        padding: '4px',
        '& svg': {
            fontSize: '20px',
        }
    },
    // Additional targeting for checked state
    '& .MuiFormControlLabel-root.Mui-checked': {
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%) !important',
        color: '#ffffff !important',
        border: '1px solid #667eea !important',
        '& .MuiFormControlLabel-label': {
            color: '#ffffff !important',
        },
        '& .MuiRadio-root': {
            color: '#ffffff !important',
        }
    }
}));

// Sleek styled container for the controls
const StyledControlsContainer = styled('div')(({ theme }) => ({
    width: '100%',
    display: 'flex',
    justifyContent: 'start',
    alignItems: 'center',
    flexWrap: 'wrap',
    columnGap: '8px',
    rowGap: '8px',
    padding: '10px 44px',
    flexDirection: 'row',
    background: 'linear-gradient(135deg, #f8f9ff 0%, #f0f2ff 100%)',
    border: '1px solid rgba(102, 126, 234, 0.2)',
    boxShadow: '0 4px 20px rgba(102, 126, 234, 0.15), 0 1px 3px rgba(102, 126, 234, 0.1)',
    backdropFilter: 'blur(10px)',
    position: 'relative',
    overflow: 'visible',
    '&::before': {
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        height: '1px',
        background: 'linear-gradient(90deg, transparent, rgba(102, 126, 234, 0.4), transparent)'
    }
}));

// Sleek close button component
const SleekCloseButton = styled(IconButton)(({ theme }) => ({
    position: 'absolute',
    top: '16px',
    right: '8px',
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    borderRadius: '6px',
    width: '28px',
    height: '28px',
    boxShadow: '0 2px 6px rgba(102, 126, 234, 0.3)',
    color: '#ffffff',
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    zIndex: 1000,
    '&:hover': {
        background: 'linear-gradient(135deg, #5a67d8 0%, #6b47b6 100%)',
        transform: 'translateY(-1px)',
        boxShadow: '0 3px 8px rgba(102, 126, 234, 0.4)',
    },
    '&:active': {
        transform: 'translateY(0px)',
        boxShadow: '0 2px 6px rgba(102, 126, 234, 0.3)',
    },
    '& svg': {
        fontSize: '16px',
    }
}));

// Compact, chip-based filter toolbar (xtram style)
const FilterBar = styled('div')(() => ({
    width: '95%',
    margin: '8px auto 0',
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: '8px',
    padding: '6px 10px',
    background: 'linear-gradient(135deg, #f8f9ff 0%, #f0f2ff 100%)',
    border: '1px solid rgba(102, 126, 234, 0.2)',
    borderRadius: '10px',
    boxShadow: '0 2px 8px rgba(102, 126, 234, 0.1)',
}));

const HeaderChip = styled(Chip)(() => ({
    height: '28px',
    fontSize: '12px',
    fontWeight: 700,
    color: '#ffffff',
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    '& .MuiChip-icon': { color: '#ffffff', fontSize: '16px' },
}));

const ToggleChip = styled(Chip, { shouldForwardProp: (prop) => prop !== 'active' })(({ active }) => ({
    height: '28px',
    fontSize: '12px',
    fontWeight: 600,
    cursor: 'pointer',
    borderRadius: '14px',
    transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
    ...(active
        ? {
            color: '#ffffff',
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            border: '1px solid transparent',
            '&:hover': { background: 'linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%)' },
        }
        : {
            color: '#2c3e50',
            backgroundColor: '#ffffff',
            border: '1px solid #d8ddff',
            '&:hover': { backgroundColor: '#f0f2ff', borderColor: '#667eea' },
        }),
}));

const ClearChip = styled(Chip)(() => ({
    height: '28px',
    fontSize: '12px',
    fontWeight: 600,
    cursor: 'pointer',
    borderRadius: '14px',
    color: '#764ba2',
    backgroundColor: '#ffffff',
    border: '1px solid #d8ddff',
    marginLeft: 'auto',
    '& .MuiChip-icon': { color: '#764ba2', fontSize: '15px' },
    '&:hover': {
        backgroundColor: '#fdeaea',
        borderColor: '#e57373',
        color: '#d32f2f',
        '& .MuiChip-icon': { color: '#d32f2f' },
    },
}));

const WithholdingModal = ({
    open,
    onClose,
    t,
    direction,
    withHoldingTaxType,
    setWithholdingTaxTypeFunc,
    isDateRange,
    startDate,
    endDate,
    setStartDate,
    setEndDate,
    date,
    setDate,
    dayjs,
    formattedAccountSourceDeductionCardOptions,
    selectedAccountCard,
    handleSelectedAccountCardChangeForWithholding,
    handleDownloadWithholdingReportReport,
    handleShowWithholdingReportReport,
    modalStyle: customModalStyle,
    client,
    partners = [],
    token
}) => {
    const [htmlContent, setHtmlContent] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [supplierSearchTerm, setSupplierSearchTerm] = useState('');
    const [showZeroBalanceCards, setShowZeroBalanceCards] = useState(true);
    const [useCreditAccount, setUseCreditAccount] = useState(false);
    const [originalHtmlContent, setOriginalHtmlContent] = useState('');
    const [supplierOptions, setSupplierOptions] = useState([]);
    const [selectedSuppliers, setSelectedSuppliers] = useState([]);
    const [activeTab, setActiveTab] = useState(0);
    const navigate = useNavigate();

    // Inject dev pattern styles
    useEffect(() => {
        const styleElement = document.createElement('style');
        styleElement.textContent = devPatternStyles;
        document.head.appendChild(styleElement);
        
        return () => {
            document.head.removeChild(styleElement);
        };
    }, []);

    const handleShowReport = useCallback(async () => {
        if (!withHoldingTaxType) return;
        
        setIsLoading(true);
        try {
            // Pass empty string for supplierNameFilter on initial load - filtering happens client-side for display
            const html = await handleShowWithholdingReportReport(showZeroBalanceCards, '', useCreditAccount);
            // Always set originalHtmlContent when fetching fresh data
            setOriginalHtmlContent(html);
            setHtmlContent(html);
            // Reset search filter when new data is loaded
            setSupplierSearchTerm('');
            setSelectedSuppliers([]);
        } catch (error) {
            console.error('Error showing report:', error);
        } finally {
            setIsLoading(false);
        }
    }, [withHoldingTaxType, showZeroBalanceCards, useCreditAccount, handleShowWithholdingReportReport]);

    // Fetch supplier/client names from backend when modal opens or withholding type changes
    useEffect(() => {
        const fetchSupplierNames = async () => {
            if (!open || !client?.caseId || !token) return;
            
            try {
                let names = [];
                
                // Determine if this is a supplier or client report based on withHoldingTaxType
                // For now, we'll fetch supplier names - you can extend this logic based on report type
                if (withHoldingTaxType && withHoldingTaxType !== 'yearlyCertificate') {
                    names = await ReportsService.getSupplierNamesForWithholdingTax(client.caseId, token);
                }
                
                // Ensure names is an array and create clean objects to avoid circular refs
                const suppliersList = Array.isArray(names) 
                    ? names
                        .filter(name => name && typeof name === 'string')
                        .map(name => {
                            // Create a plain object with only value and label
                            return {
                                value: String(name),
                                label: String(name)
                            };
                        })
                    : [];
                    
                setSupplierOptions(suppliersList);
            } catch (error) {
                console.error('Error fetching supplier names:', error);
                setSupplierOptions([]);
            }
        };
        
        fetchSupplierNames();
    }, [open, client?.caseId, token, withHoldingTaxType]);

    // Navigation handler for row clicks
    const handleRowClick = useCallback(async (accountCardId, accountCardName, palCode) => {
        
        if (!client || !accountCardId || !accountCardName) {
            return;
        }

        try {
            const navigationState = {
                client,
                accountCardId: accountCardId,
                accountCardName: accountCardName,
                palCodeName: palCode || 'NonPalCode', // Use dynamic palCode or fallback
                startDate,
                endDate,
                isSortCodeReport: true,
                breadcrumbs: ['Reports', 'LedgerReport'],
            };
            

            
            // Navigate to AccountCardDashboard with the data
            navigate('/AccountCardDashboard', {
                state: navigationState,
            });
        } catch (error) {
            console.error('Error navigating to AccountCardDashboard:', error);
        }
    }, [client, startDate, endDate, navigate]);

    // Filter function to apply search and zero balance filters
    const applyFilters = useCallback(() => {
        if (!originalHtmlContent) return;

        // Create a temporary DOM element to parse the HTML
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = originalHtmlContent;

        // Check if this is a multi-table report (monthly report with showMode=true)
        const allTables = tempDiv.querySelectorAll('table');
        const isMultiTableReport = allTables.length > 1;

        // Get selected supplier names array
        const selectedSupplierNames = selectedSuppliers.map(s => s.value || s);

        if (isMultiTableReport) {
            // Handle multi-table reports (monthly with showMode=true)
            
            // Process each table separately
            allTables.forEach((table, tableIndex) => {
                const tbody = table.querySelector('tbody');
                if (!tbody) return;

                // Get all rows from this table
                const allRows = tbody.querySelectorAll('tr');
                const dataRows = Array.from(allRows).filter(row => {
                    const firstCell = row.querySelector('td:first-child');
                    return firstCell && 
                           !firstCell.textContent.includes('סה״כ בחודש') && 
                           !firstCell.textContent.includes('סה״כ בדוח') &&
                           !firstCell.textContent.includes('מספר ספקים');
                });

                let filteredRows = Array.from(dataRows);

                // Apply both supplier search filter and zero balance filter together
                filteredRows = filteredRows.filter(row => {
                    // Apply supplier search filter (exact match for multiple suppliers)
                    if (selectedSupplierNames.length > 0) {
                        const supplierNameCell = row.querySelector('td:first-child');
                        if (supplierNameCell) {
                            const supplierName = supplierNameCell.textContent.trim();
                            if (!selectedSupplierNames.includes(supplierName)) {
                                return false; // Skip this row if it doesn't match any selected supplier
                            }
                        }
                    }

                    // Apply zero balance filter
                    if (!showZeroBalanceCards) {
                        const cells = row.querySelectorAll('td');
                        if (cells.length === 3) {
                            // 3-column structure
                            const totalBalanceText = cells[1]?.textContent?.trim() || '0';
                            const totalWithholdingText = cells[2]?.textContent?.trim() || '0';
                            
                            const isBalanceNegative = totalBalanceText.includes('(') && totalBalanceText.includes(')');
                            const isWithholdingNegative = totalWithholdingText.includes('(') && totalWithholdingText.includes(')');
                            
                            const cleanBalanceText = totalBalanceText.replace(/[(),]/g, '');
                            const cleanWithholdingText = totalWithholdingText.replace(/[(),]/g, '');
                            
                            const totalBalance = parseFloat(cleanBalanceText) * (isBalanceNegative ? -1 : 1) || 0;
                            const totalWithholding = parseFloat(cleanWithholdingText) * (isWithholdingNegative ? -1 : 1) || 0;
                            
                            return totalBalance !== 0 || totalWithholding !== 0;
                        } else if (cells.length === 5) {
                            // 5-column structure
                            const totalBalanceText = cells[3]?.textContent?.trim() || '0';
                            const totalWithholdingText = cells[4]?.textContent?.trim() || '0';
                            
                            const isBalanceNegative = totalBalanceText.includes('(') && totalBalanceText.includes(')');
                            const isWithholdingNegative = totalWithholdingText.includes('(') && totalWithholdingText.includes(')');
                            
                            const cleanBalanceText = totalBalanceText.replace(/[(),]/g, '');
                            const cleanWithholdingText = totalWithholdingText.replace(/[(),]/g, '');
                            
                            const totalBalance = parseFloat(cleanBalanceText) * (isBalanceNegative ? -1 : 1) || 0;
                            const totalWithholding = parseFloat(cleanWithholdingText) * (isWithholdingNegative ? -1 : 1) || 0;
                            
                            return totalBalance !== 0 || totalWithholding !== 0;
                        }
                        return true;
                    }
                    
                    return true; // If showZeroBalanceCards is true, include all rows
                });

                // Clear existing rows and add filtered rows
                tbody.innerHTML = '';
                filteredRows.forEach(row => {
                    const clonedRow = row.cloneNode(true);
                    tbody.appendChild(clonedRow);
                });

                // Add monthly subtotal if this is not the last table (final totals table)
                if (tableIndex < allTables.length - 1) {
                    const monthName = table.previousElementSibling?.querySelector('p')?.textContent?.trim() || '';
                    if (monthName) {
                        // Calculate totals for this month
                        let monthBalance = 0;
                        let monthWithholding = 0;
                        
                        filteredRows.forEach(row => {
                            const cells = row.querySelectorAll('td');
                            if (cells.length === 3) {
                                const balanceText = cells[1]?.textContent?.trim() || '0';
                                const withholdingText = cells[2]?.textContent?.trim() || '0';
                                
                                const isBalanceNegative = balanceText.includes('(') && balanceText.includes(')');
                                const isWithholdingNegative = withholdingText.includes('(') && withholdingText.includes(')');
                                
                                const cleanBalanceText = balanceText.replace(/[(),]/g, '');
                                const cleanWithholdingText = withholdingText.replace(/[(),]/g, '');
                                
                                const balance = parseFloat(cleanBalanceText) * (isBalanceNegative ? -1 : 1) || 0;
                                const withholding = parseFloat(cleanWithholdingText) * (isWithholdingNegative ? -1 : 1) || 0;
                                
                                monthBalance += balance;
                                monthWithholding += withholding;
                            } else if (cells.length === 5) {
                                const balanceText = cells[3]?.textContent?.trim() || '0';
                                const withholdingText = cells[4]?.textContent?.trim() || '0';
                                
                                const isBalanceNegative = balanceText.includes('(') && balanceText.includes(')');
                                const isWithholdingNegative = withholdingText.includes('(') && withholdingText.includes(')');
                                
                                const cleanBalanceText = balanceText.replace(/[(),]/g, '');
                                const cleanWithholdingText = withholdingText.replace(/[(),]/g, '');
                                
                                const balance = parseFloat(cleanBalanceText) * (isBalanceNegative ? -1 : 1) || 0;
                                const withholding = parseFloat(cleanWithholdingText) * (isWithholdingNegative ? -1 : 1) || 0;
                                
                                monthBalance += balance;
                                monthWithholding += withholding;
                            }
                        });
                        
                        // Always add monthly subtotal
                        const subtotalRow = document.createElement('tr');
                        subtotalRow.style.borderTop = '1px solid black';
                        subtotalRow.innerHTML = `
                            <td style="font-weight: bold;">סה״כ בחודש ${monthName}:</td>
                            <td style="font-weight: bold;">${monthBalance.toLocaleString('he-IL')}</td>
                            <td style="font-weight: bold;">${monthWithholding.toLocaleString('he-IL')}</td>
                        `;
                        tbody.appendChild(subtotalRow);
                    }
                } else {
                    // This is the final totals table - calculate overall totals
                    let totalBalance = 0;
                    let totalWithholding = 0;
                    let supplierCount = 0;

                    // Calculate totals from all previous tables
                    for (let i = 0; i < allTables.length - 1; i++) {
                        const prevTable = allTables[i];
                        const prevTbody = prevTable.querySelector('tbody');
                        if (prevTbody) {
                            const prevRows = prevTbody.querySelectorAll('tr');
                            prevRows.forEach(row => {
                                const cells = row.querySelectorAll('td');
                                if ((cells.length === 3 || cells.length === 5) && !cells[0].textContent.includes('סה״כ בחודש')) {
                                    supplierCount++;
                                    
                                    let balanceText, withholdingText;
                                    if (cells.length === 3) {
                                        balanceText = cells[1]?.textContent?.trim() || '0';
                                        withholdingText = cells[2]?.textContent?.trim() || '0';
                                    } else if (cells.length === 5) {
                                        balanceText = cells[3]?.textContent?.trim() || '0';
                                        withholdingText = cells[4]?.textContent?.trim() || '0';
                                    }
                                    
                                    const isBalanceNegative = balanceText.includes('(') && balanceText.includes(')');
                                    const isWithholdingNegative = withholdingText.includes('(') && withholdingText.includes(')');
                                    
                                    const cleanBalanceText = balanceText.replace(/[(),]/g, '');
                                    const cleanWithholdingText = withholdingText.replace(/[(),]/g, '');
                                    
                                    const balance = parseFloat(cleanBalanceText) * (isBalanceNegative ? -1 : 1) || 0;
                                    const withholding = parseFloat(cleanWithholdingText) * (isWithholdingNegative ? -1 : 1) || 0;
                                    
                                    totalBalance += balance;
                                    totalWithholding += withholding;
                                }
                            });
                        }
                    }
                    
                    // Always add final totals (even when filtered)
                    const totalsRow = document.createElement('tr');
                    totalsRow.style.borderTop = '2px solid black';
                    totalsRow.innerHTML = `
                        <td style="font-weight: bold; font-size: 16px; padding-top: 15px;">סה״כ בדוח:</td>
                        <td style="font-weight: bold; font-size: 16px; padding-top: 15px;">${totalBalance.toLocaleString('he-IL')}</td>
                        <td style="font-weight: bold; font-size: 16px; padding-top: 15px;">${totalWithholding.toLocaleString('he-IL')}</td>
                    `;
                    tbody.appendChild(totalsRow);
                    
                    // Add supplier count
                    const countRow = document.createElement('tr');
                    countRow.innerHTML = `
                        <td colspan="3" style="font-weight: bold; font-size: 14px; padding-top: 10px; text-align: right;">מספר ספקים בדוח: ${supplierCount}</td>
                    `;
                    tbody.appendChild(countRow);

                    // const clientCountRow = document.createElement('tr');
                    // clientCountRow.style.borderTop = '1px solid black';
                    // clientCountRow.innerHTML = `
                    //     <td style="font-weight: bold;">מספר ספקים עם ניכוי מס: ${supplierCount}</td>
                    // `;
                    // tbody.appendChild(clientCountRow);
                }
            });
        } else {
            // Handle single table reports (yearly reports or monthly with showMode=false)
            const allRows = tempDiv.querySelectorAll('tbody tr');
            const dataRows = Array.from(allRows).filter(row => {
                const firstCell = row.querySelector('td:first-child');
                return firstCell && !firstCell.textContent.includes('סה״כ ספקים בדוח');
            });
        
            let filteredRows = Array.from(dataRows);

            // Apply both supplier search filter and zero balance filter together
            filteredRows = filteredRows.filter(row => {
                // Apply supplier search filter (exact match for multiple suppliers)
                if (selectedSupplierNames.length > 0) {
                    const supplierNameCell = row.querySelector('td:first-child');
                    if (supplierNameCell) {
                        const supplierName = supplierNameCell.textContent.trim();
                        if (!selectedSupplierNames.includes(supplierName)) {
                            return false; // Skip this row if it doesn't match any selected supplier
                        }
                    }
                }

                // Apply zero balance filter
                if (!showZeroBalanceCards) {
                    const cells = row.querySelectorAll('td');
                    
                    if (cells.length === 3) {
                        const totalBalanceText = cells[1]?.textContent?.trim() || '0';
                        const totalWithholdingText = cells[2]?.textContent?.trim() || '0';
                        
                        const isBalanceNegative = totalBalanceText.includes('(') && totalBalanceText.includes(')');
                        const isWithholdingNegative = totalWithholdingText.includes('(') && totalWithholdingText.includes(')');
                        
                        const cleanBalanceText = totalBalanceText.replace(/[(),]/g, '');
                        const cleanWithholdingText = totalWithholdingText.replace(/[(),]/g, '');
                        
                        const totalBalance = parseFloat(cleanBalanceText) * (isBalanceNegative ? -1 : 1) || 0;
                        const totalWithholding = parseFloat(cleanWithholdingText) * (isWithholdingNegative ? -1 : 1) || 0;
                        
                        return totalBalance !== 0 || totalWithholding !== 0;
                    } else if (cells.length === 5) {
                        const totalBalanceText = cells[3]?.textContent?.trim() || '0';
                        const totalWithholdingText = cells[4]?.textContent?.trim() || '0';
                        
                        const isBalanceNegative = totalBalanceText.includes('(') && totalBalanceText.includes(')');
                        const isWithholdingNegative = totalWithholdingText.includes('(') && totalWithholdingText.includes(')');
                        
                        const cleanBalanceText = totalBalanceText.replace(/[(),]/g, '');
                        const cleanWithholdingText = totalWithholdingText.replace(/[(),]/g, '');
                        
                        const totalBalance = parseFloat(cleanBalanceText) * (isBalanceNegative ? -1 : 1) || 0;
                        const totalWithholding = parseFloat(cleanWithholdingText) * (isWithholdingNegative ? -1 : 1) || 0;
                        
                        return totalBalance !== 0 || totalWithholding !== 0;
                    }
                    return true;
                }
                
                return true; // If showZeroBalanceCards is true, include all rows
            });

            // Rebuild the table with filtered rows
            const tbody = tempDiv.querySelector('tbody');
            if (tbody) {
                tbody.innerHTML = '';
                filteredRows.forEach(row => {
                    const clonedRow = row.cloneNode(true);
                    tbody.appendChild(clonedRow);
                });

                // Calculate totals from filtered data rows only
                let totalBalance = 0;
                let totalWithholding = 0;
                let supplierCount = filteredRows.length;

                filteredRows.forEach(row => {
                    const cells = row.querySelectorAll('td');
                    
                    if (cells.length === 3) {
                        const balanceText = cells[1]?.textContent?.trim() || '0';
                        const withholdingText = cells[2]?.textContent?.trim() || '0';
                        
                        const isBalanceNegative = balanceText.includes('(') && balanceText.includes(')');
                        const isWithholdingNegative = withholdingText.includes('(') && withholdingText.includes(')');
                        
                        const cleanBalanceText = balanceText.replace(/[(),]/g, '');
                        const cleanWithholdingText = withholdingText.replace(/[(),]/g, '');
                        
                        const balance = parseFloat(cleanBalanceText) * (isBalanceNegative ? -1 : 1) || 0;
                        const withholding = parseFloat(cleanWithholdingText) * (isWithholdingNegative ? -1 : 1) || 0;
                        
                        totalBalance += balance;
                        totalWithholding += withholding;
                    } else if (cells.length === 5) {
                        const balanceText = cells[3]?.textContent?.trim() || '0';
                        const withholdingText = cells[4]?.textContent?.trim() || '0';
                        
                        const isBalanceNegative = balanceText.includes('(') && balanceText.includes(')');
                        const isWithholdingNegative = withholdingText.includes('(') && withholdingText.includes(')');
                        
                        const cleanBalanceText = balanceText.replace(/[(),]/g, '');
                        const cleanWithholdingText = withholdingText.replace(/[(),]/g, '');
                        
                        const balance = parseFloat(cleanBalanceText) * (isBalanceNegative ? -1 : 1) || 0;
                        const withholding = parseFloat(cleanWithholdingText) * (isWithholdingNegative ? -1 : 1) || 0;
                        
                        totalBalance += balance;
                        totalWithholding += withholding;
                    }
                });

                // Add totals row with enhanced styling
                const totalsRow = document.createElement('tr');
                totalsRow.style.borderTop = '2px solid black';
                
                const firstRowCells = filteredRows.length > 0 ? filteredRows[0].querySelectorAll('td').length : 0;
                
                if (firstRowCells === 3) {
                    totalsRow.innerHTML = `
                        <td colspan="1" style="font-weight: bold; font-size: 14px; padding-top: 10px;">סה״כ ספקים בדוח: ${supplierCount}</td>
                        <td style="font-weight: bold; font-size: 16px; padding-top: 15px;">${totalBalance.toLocaleString('he-IL')}</td>
                        <td style="font-weight: bold; font-size: 16px; padding-top: 15px;">${totalWithholding.toLocaleString('he-IL')}</td>
                    `;
                } else if (firstRowCells === 5) {
                    totalsRow.innerHTML = `
                        <td colspan="3" style="font-weight: bold; font-size: 14px; padding-top: 10px;">סה״כ ספקים בדוח: ${supplierCount}</td>
                        <td style="font-weight: bold; font-size: 16px; padding-top: 15px;">${totalBalance.toLocaleString('he-IL')}</td>
                        <td style="font-weight: bold; font-size: 16px; padding-top: 15px;">${totalWithholding.toLocaleString('he-IL')}</td>
                    `;
                } else {
                    totalsRow.innerHTML = `
                        <td style="font-weight: bold; font-size: 14px; padding-top: 10px;">סה״כ ספקים בדוח: ${supplierCount}</td>
                    `;
                }
                tbody.appendChild(totalsRow);
            }
        }

        // Update the displayed content
        setHtmlContent(tempDiv.innerHTML);
    }, [originalHtmlContent, selectedSuppliers, showZeroBalanceCards]);

    // Apply filters when selected suppliers or zero balance toggle changes
    useEffect(() => {
        if (originalHtmlContent) {
            if (selectedSuppliers && selectedSuppliers.length > 0) {
                // Apply filters when suppliers are selected
                applyFilters();
            } else {
                // If no suppliers selected, show original content
                setHtmlContent(originalHtmlContent);
            }
        }
    }, [applyFilters, selectedSuppliers, originalHtmlContent]);

    return (
    <Modal 
        open={open} 
        onClose={onClose} 
        aria-labelledby="withholding-modal-title" 
        aria-describedby="withholding-modal-description"
    >
        <Box sx={modalStyle} className={`${direction}`}>

            <Tabs
                value={activeTab}
                onChange={(_, v) => setActiveTab(v)}
                sx={{
                    width: '95%',
                    minHeight: 40,
                    mt: 1,
                    mb: 0.5,
                    '& .MuiTab-root': {
                        minHeight: 40,
                        textTransform: 'none',
                        fontWeight: 700,
                        fontSize: '13px',
                    },
                }}
            >
                <Tab label={t('WithholdingReportsTab') || t('Reports') || 'Reports'} />
                <Tab label={t('CreatePaymentsTab') || 'Create Payments'} />
            </Tabs>

            <SleekCloseButton onClick={onClose} aria-label="Close modal">
                <CloseIcon />
            </SleekCloseButton>

            {activeTab === 1 ? (
                <Box sx={{ width: '100%', flex: 1, minHeight: 0, display: 'flex', justifyContent: 'center', pb: 1 }}>
                    <WithholdingUnpaidPaymentsTab
                        t={t}
                        direction={direction}
                        client={client}
                        token={token}
                        startDate={startDate}
                        endDate={endDate}
                        setStartDate={setStartDate}
                        setEndDate={setEndDate}
                    />
                </Box>
            ) : (
            <>
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%' }} className={`${direction}`}>
                <StyledControlsContainer className="pdf-view-upper">
                    <h3 style={{ 
                        margin: 0,
                        fontSize: '14px', 
                        fontWeight: '600', 
                        color: '#2c3e50',
                        minWidth: 'fit-content',
                        whiteSpace: 'nowrap'
                    }}>
                        {t('SelectReportType')}:
                    </h3>
                    <StyledRadioGroup
                        value={withHoldingTaxType}
                        onChange={(event) => setWithholdingTaxTypeFunc(event.target.value)}
                        row
                        aria-label="report-type"
                    >
                        <FormControlLabel
                            value="yearlyCertificate"
                            control={<Radio />}
                            label={t('AnnualWithholdingTaxCertificate')}
                        />
                        <FormControlLabel
                            value="yearlyReport"
                            control={<Radio />}
                            label={t('AnnualWithholdingTaxReport')}
                        />
                        <FormControlLabel
                            value="monthlyReport"
                            control={<Radio />}
                            label={t('PeriodicWithholdingTaxReport')}
                        />
                        <FormControlLabel
                            value="monthlyPeriodReport"
                            control={<Radio />}
                            label={t('PeriodicReportByMonths')}
                        />
                    </StyledRadioGroup>



                    {isDateRange ? (
                        <>
                            <div style={{ minWidth: '120px', maxWidth: '130px' }}>
                                <CustomMonthDatePicker
                                    onDateChange={setStartDate}
                                    date={startDate}
                                    label={t('StartDate')}
                                    height="40px"
                                />
                            </div>
                            <div style={{ minWidth: '120px', maxWidth: '130px' }}>
                                <CustomMonthDatePicker
                                    onDateChange={setEndDate}
                                    date={endDate}
                                    label={t('EndDate')}
                                    height="40px"
                                />
                            </div>
                        </>
                    ) : (
                        <div style={{ minWidth: '120px', maxWidth: '150px' }}>
                            <CustomYearDatePicker
                                onDateChange={setDate}
                                date={date}
                                label={t("date")}
                                height="40px"
                            />
                        </div>
                    )}

                    {withHoldingTaxType === 'yearlyCertificate' && (
                        <div style={{ minWidth: '180px', maxWidth: '200px' }}>
                            <AutocompleteSelect
                                options={formattedAccountSourceDeductionCardOptions}
                                selectedValues={selectedAccountCard}
                                onChange={handleSelectedAccountCardChangeForWithholding}
                                label={t("Select AccountCard")}
                                placeholder={t("Select AccountCard")}
                                isMultiple={false}
                                width="100%"
                                
                            />
                        </div>
                    )}

                    {/* Report option: use credit account (kept with the report config controls) */}
                    <FormControlLabel
                        control={
                            <Checkbox
                                size="small"
                                checked={useCreditAccount}
                                onChange={async (e) => {
                                    const v = e.target.checked;
                                    setUseCreditAccount(v);
                                    if (withHoldingTaxType && originalHtmlContent) {
                                        setIsLoading(true);
                                        try {
                                            const html = await handleShowWithholdingReportReport(showZeroBalanceCards, '', v);
                                            setOriginalHtmlContent(html);
                                            setHtmlContent(html);
                                            setSupplierSearchTerm('');
                                            setSelectedSuppliers([]);
                                        } catch (error) {
                                            console.error('Error refreshing report (credit account):', error);
                                        } finally {
                                            setIsLoading(false);
                                        }
                                    }
                                }}
                                sx={{
                                    color: '#667eea',
                                    padding: '4px',
                                    '&.Mui-checked': { color: '#667eea' },
                                    '& svg': { fontSize: '20px' },
                                }}
                            />
                        }
                        label={t('UseCreditAccount')}
                        sx={{
                            margin: 0,
                            padding: '2px 10px',
                            borderRadius: '14px',
                            backgroundColor: '#ffffff',
                            border: useCreditAccount ? '1px solid #667eea' : '1px solid #d8ddff',
                            transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
                            '&:hover': { backgroundColor: '#f0f2ff' },
                            '& .MuiFormControlLabel-label': { fontSize: '12px', fontWeight: 600, color: '#2c3e50', whiteSpace: 'nowrap' },
                        }}
                    />

                    {/* Action buttons grouped together */}
                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        flexWrap: 'wrap'
                    }}>
                        <SleekButton
                            onClick={handleShowReport}
                            disabled={!withHoldingTaxType || isLoading}
                            startIcon={<VisibilityIcon />}
                            sx={{
                                backgroundColor: !withHoldingTaxType ? '#ccc' : '',
                                '&:hover': {
                                    backgroundColor: !withHoldingTaxType ? '#ccc' : '',
                                }
                            }}
                        >
                            {isLoading ? t('Loading') : t('ViewData')}
                        </SleekButton>

                        <SleekButton
                            onClick={() => {
                                // Convert selected suppliers array to comma-separated string for backend
                                const supplierNames = selectedSuppliers.map(s => s.value || s).join(',');
                                handleDownloadWithholdingReportReport(showZeroBalanceCards, supplierNames, useCreditAccount);
                            }}
                            disabled={!withHoldingTaxType}
                            startIcon={<DownloadIcon />}
                            sx={{
                                backgroundColor: !withHoldingTaxType ? '#ccc' : '',
                                '&:hover': {
                                    backgroundColor: !withHoldingTaxType ? '#ccc' : '',
                                }
                            }}
                        >
                            {t('Download')}
                        </SleekButton>
                    </div>
                </StyledControlsContainer>
            </div>

            {/* Filter Section - Only show when data is loaded */}
            {htmlContent && (
                <FilterBar className={`${direction}`}>
                    <HeaderChip icon={<FilterAltIcon />} label={t('Filters')} />

                    {/* Supplier Autocomplete (renders selected as chips) */}
                    <div style={{ flex: '1 1 240px', minWidth: '200px', maxWidth: '380px' }}>
                        <AutocompleteSelect
                            options={supplierOptions}
                            selectedValues={selectedSuppliers}
                            onChange={(event, newValue) => {
                                // Handle the value safely to avoid circular structure
                                if (!newValue || (Array.isArray(newValue) && newValue.length === 0)) {
                                    setSelectedSuppliers([]);
                                    return;
                                }

                                // Ensure we have a clean array of objects
                                const safeValue = Array.isArray(newValue)
                                    ? newValue.map(v => {
                                        if (typeof v === 'string') {
                                            return { value: v, label: v };
                                        }
                                        // Extract only value and label to avoid circular refs
                                        return {
                                            value: v.value || v,
                                            label: v.label || v
                                        };
                                    })
                                    : [{
                                        value: newValue.value || newValue,
                                        label: newValue.label || newValue
                                    }];

                                setSelectedSuppliers(safeValue);
                            }}
                            label=""
                            placeholder={direction === 'rtl' ? 'בחר ספקים' : 'Select Suppliers'}
                            isMultiple={true}
                            width="100%"
                            direction={direction}
                        />
                    </div>

                    {/* Zero Balance Toggle Chip */}
                    <ToggleChip
                        active={showZeroBalanceCards}
                        label={t('ShowZeroBalance')}
                        onClick={async () => {
                            const newValue = !showZeroBalanceCards;
                            setShowZeroBalanceCards(newValue);
                            // Trigger new API call with updated filter - filtering happens client-side for display
                            if (withHoldingTaxType && originalHtmlContent) {
                                setIsLoading(true);
                                try {
                                    const html = await handleShowWithholdingReportReport(newValue, '', useCreditAccount);
                                    setOriginalHtmlContent(html);
                                    setHtmlContent(html);
                                    setSupplierSearchTerm('');
                                    setSelectedSuppliers([]);
                                } catch (error) {
                                    console.error('Error updating report with filter:', error);
                                } finally {
                                    setIsLoading(false);
                                }
                            }
                        }}
                    />

                    {/* Clear Filters Chip */}
                    <ClearChip
                        icon={<ClearAllIcon />}
                        label={t('ClearFilters')}
                        onClick={() => {
                            setSupplierSearchTerm('');
                            setSelectedSuppliers([]);
                            setShowZeroBalanceCards(true);
                        }}
                    />
                </FilterBar>
            )}

            {/* HTML Report Display or Placeholder */}
            {htmlContent ? (
                <div className="dev-pattern-wrapper" style={{ 
                    width: '95%',
                    height: '90%',
                    backgroundColor: '#ffffff'
                }}>
                    <iframe 
                        srcDoc={htmlContent}
                        style={{
                            width: '100%',
                            height: '100%',
                            border: 'none'
                        }}
                        title="Report Preview"
                        sandbox="allow-same-origin"
                        onLoad={(e) => {
                            // Add click handlers to table rows in the iframe
                            const iframe = e.target;
                            const iframeDoc = iframe.contentDocument || iframe.contentWindow.document;
                            
                            if (iframeDoc) {
                                const allRows = iframeDoc.querySelectorAll('tr');
                                
                                const rows = iframeDoc.querySelectorAll('tr.tr');
                                
                                // Also try to find rows without the .tr class
                                const allTableRows = iframeDoc.querySelectorAll('tbody tr');
                                
                                allTableRows.forEach((row, index) => {
                                    const accountCardId = row.getAttribute('data-id');
                                    const accountCardName = row.querySelector('td:first-child')?.textContent?.trim();
                                    const palCode = row.getAttribute('data-type');
                                    
                                    // Also check if the row has the .tr class
                                    const hasTrClass = row.classList.contains('tr');
                                    
                                    if (accountCardId && accountCardName) {
                                        row.style.cursor = 'pointer';
                                        row.style.transition = 'background-color 0.2s ease';
                                        
                                        row.addEventListener('click', () => {
                                            handleRowClick(accountCardId, accountCardName, palCode);
                                        });
                                        
                                        // Add hover effects
                                        row.addEventListener('mouseenter', () => {
                                            row.style.backgroundColor = '';
                                        });

                                    }
                                });
                            }
                        }}
                    />
                </div>
            ) : (
                <div className="dev-pattern-wrapper" style={{ 
                    width: '90%',
                    height: '90%',
                    backgroundColor: '#ffffff',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '40px'
                }}>
                    {/* Enhanced Animated SVG Report Icon with Arrow */}
                    <div style={{ position: 'relative', display: 'inline-block' }}>
                        {/* Arrow pointing up */}
                        <svg 
                            width="40" 
                            height="40" 
                            viewBox="0 0 40 40" 
                            style={{ 
                                position: 'absolute',
                                top: '-15px',
                                left: '50%',
                                transform: 'translateX(-50%)',
                                zIndex: 10,
                                animation: 'bounce 2s ease-in-out infinite'
                            }}
                        >
                            <defs>
                                <linearGradient id="arrowGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                                    <stop offset="0%" style={{stopColor:'#667eea', stopOpacity:1}} />
                                    <stop offset="100%" style={{stopColor:'#764ba2', stopOpacity:1}} />
                                </linearGradient>
                                <filter id="arrowShadow" x="-50%" y="-50%" width="200%" height="200%">
                                    <feDropShadow dx="2" dy="3" stdDeviation="2" floodColor="#667eea" floodOpacity="0.4"/>
                                </filter>
                            </defs>
                            <path 
                                d="M20 5 L35 25 L30 25 L30 35 L10 35 L10 25 L5 25 Z" 
                                fill="url(#arrowGradient)" 
                                stroke="#ffffff" 
                                strokeWidth="1"
                                filter="url(#arrowShadow)"
                            />
                        </svg>

                        {/* Main Report Icon */}
                        <svg 
                            width="180" 
                            height="180" 
                            viewBox="0 0 180 180" 
                            style={{ 
                                marginBottom: '30px',
                                animation: 'pulse 3s ease-in-out infinite, float 4s ease-in-out infinite'
                            }}
                        >
                            {/* Enhanced Document Background with better shadows */}
                            <defs>
                                <filter id="shadow" x="-30%" y="-30%" width="160%" height="160%">
                                    <feDropShadow dx="6" dy="8" stdDeviation="6" floodColor="#667eea" floodOpacity="0.4"/>
                                    <feDropShadow dx="2" dy="4" stdDeviation="3" floodColor="#764ba2" floodOpacity="0.2"/>
                                </filter>
                                <linearGradient id="documentGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                                    <stop offset="0%" style={{stopColor:'#ffffff', stopOpacity:1}} />
                                    <stop offset="50%" style={{stopColor:'#f8f9ff', stopOpacity:1}} />
                                    <stop offset="100%" style={{stopColor:'#e3e8ff', stopOpacity:1}} />
                                </linearGradient>
                                <linearGradient id="headerGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                                    <stop offset="0%" style={{stopColor:'#667eea', stopOpacity:1}} />
                                    <stop offset="100%" style={{stopColor:'#764ba2', stopOpacity:1}} />
                                </linearGradient>
                                <linearGradient id="chartGradient1" x1="0%" y1="0%" x2="100%" y2="100%">
                                    <stop offset="0%" style={{stopColor:'#667eea', stopOpacity:1}} />
                                    <stop offset="100%" style={{stopColor:'#5a6fd8', stopOpacity:1}} />
                                </linearGradient>
                                <linearGradient id="chartGradient2" x1="0%" y1="0%" x2="100%" y2="100%">
                                    <stop offset="0%" style={{stopColor:'#764ba2', stopOpacity:1}} />
                                    <stop offset="100%" style={{stopColor:'#6b4190', stopOpacity:1}} />
                                </linearGradient>
                            </defs>
                            
                            {/* Main document with enhanced styling */}
                            <rect 
                                x="30" 
                                y="25" 
                                width="120" 
                                height="130" 
                                rx="8" 
                                fill="url(#documentGradient)" 
                                stroke="url(#headerGradient)" 
                                strokeWidth="3"
                                filter="url(#shadow)"
                            />
                            
                            {/* Document header with gradient */}
                            <rect 
                                x="35" 
                                y="30" 
                                width="110" 
                                height="15" 
                                rx="4" 
                                fill="url(#headerGradient)"
                            />
                            
                            {/* Document title text */}
                            <text 
                                x="90" 
                                y="41" 
                                textAnchor="middle" 
                                fontSize="8" 
                                fill="#ffffff" 
                                fontWeight="bold"
                                fontFamily="Arial, sans-serif"
                            >
                                {t('Reports')}
                            </text>
                            
                            {/* Enhanced Document Lines with better animation */}
                            <rect x="35" y="55" width="80" height="3" rx="1.5" fill="#c7d2fe" opacity="0.8">
                                <animate attributeName="width" values="80;100;80" dur="3s" repeatCount="indefinite" />
                                <animate attributeName="opacity" values="0.8;1;0.8" dur="3s" repeatCount="indefinite" />
                            </rect>
                            <rect x="35" y="63" width="90" height="3" rx="1.5" fill="#a5b4fc" opacity="0.9">
                                <animate attributeName="width" values="90;110;90" dur="3s" begin="0.5s" repeatCount="indefinite" />
                                <animate attributeName="opacity" values="0.9;1;0.9" dur="3s" begin="0.5s" repeatCount="indefinite" />
                            </rect>
                            <rect x="35" y="71" width="70" height="3" rx="1.5" fill="#8b9cfb" opacity="0.7">
                                <animate attributeName="width" values="70;90;70" dur="3s" begin="1s" repeatCount="indefinite" />
                                <animate attributeName="opacity" values="0.7;1;0.7" dur="3s" begin="1s" repeatCount="indefinite" />
                            </rect>
                            <rect x="35" y="79" width="85" height="3" rx="1.5" fill="#c7d2fe" opacity="0.8">
                                <animate attributeName="width" values="85;105;85" dur="3s" begin="1.5s" repeatCount="indefinite" />
                                <animate attributeName="opacity" values="0.8;1;0.8" dur="3s" begin="1.5s" repeatCount="indefinite" />
                            </rect>
                            
                            {/* Enhanced Chart/Graph Icon */}
                            <g transform="translate(75, 95)">
                                {/* Chart background */}
                                <rect x="-5" y="0" width="80" height="50" rx="4" fill="#f1f5ff" opacity="0.3" />
                                
                                {/* Bar Chart with improved animations and gradients */}
                                <rect x="5" y="30" width="12" height="20" fill="url(#chartGradient1)" rx="3">
                                    <animate attributeName="height" values="20;35;20" dur="2.8s" repeatCount="indefinite" />
                                    <animate attributeName="y" values="30;15;30" dur="2.8s" repeatCount="indefinite" />
                                </rect>
                                <rect x="23" y="25" width="12" height="25" fill="url(#chartGradient2)" rx="3">
                                    <animate attributeName="height" values="25;40;25" dur="2.8s" begin="0.7s" repeatCount="indefinite" />
                                    <animate attributeName="y" values="25;10;25" dur="2.8s" begin="0.7s" repeatCount="indefinite" />
                                </rect>
                                <rect x="41" y="20" width="12" height="30" fill="url(#chartGradient1)" rx="3">
                                    <animate attributeName="height" values="30;45;30" dur="2.8s" begin="1.4s" repeatCount="indefinite" />
                                    <animate attributeName="y" values="20;5;20" dur="2.8s" begin="1.4s" repeatCount="indefinite" />
                                </rect>
                                <rect x="59" y="27" width="12" height="23" fill="url(#chartGradient2)" rx="3">
                                    <animate attributeName="height" values="23;38;23" dur="2.8s" begin="2.1s" repeatCount="indefinite" />
                                    <animate attributeName="y" values="27;12;27" dur="2.8s" begin="2.1s" repeatCount="indefinite" />
                                </rect>
                            </g>
                            
                            {/* Enhanced Floating Elements with better positioning */}
                            <circle cx="135" cy="45" r="5" fill="url(#chartGradient1)" opacity="0.8">
                                <animate attributeName="cy" values="45;35;45" dur="4s" repeatCount="indefinite" />
                                <animate attributeName="r" values="5;7;5" dur="4s" repeatCount="indefinite" />
                                <animate attributeName="opacity" values="0.8;1;0.8" dur="4s" repeatCount="indefinite" />
                            </circle>
                            <circle cx="25" cy="100" r="4" fill="url(#chartGradient2)" opacity="0.7">
                                <animate attributeName="cy" values="100;90;100" dur="3.5s" begin="1s" repeatCount="indefinite" />
                                <animate attributeName="r" values="4;6;4" dur="3.5s" begin="1s" repeatCount="indefinite" />
                                <animate attributeName="opacity" values="0.7;1;0.7" dur="3.5s" begin="1s" repeatCount="indefinite" />
                            </circle>
                            <circle cx="145" cy="130" r="3" fill="url(#chartGradient1)" opacity="0.6">
                                <animate attributeName="cy" values="130;120;130" dur="3s" begin="2s" repeatCount="indefinite" />
                                <animate attributeName="opacity" values="0.6;1;0.6" dur="3s" begin="2s" repeatCount="indefinite" />
                            </circle>
                            
                            {/* Additional floating data points with enhanced styling */}
                            <circle cx="20" cy="60" r="3" fill="url(#chartGradient2)" opacity="0.6">
                                <animate attributeName="cy" values="60;50;60" dur="2.5s" begin="1.2s" repeatCount="indefinite" />
                                <animate attributeName="r" values="3;5;3" dur="2.5s" begin="1.2s" repeatCount="indefinite" />
                            </circle>
                            <circle cx="155" cy="85" r="3.5" fill="url(#chartGradient1)" opacity="0.7">
                                <animate attributeName="cy" values="85;75;85" dur="2.8s" begin="1.8s" repeatCount="indefinite" />
                                <animate attributeName="r" values="3.5;5.5;3.5" dur="2.8s" begin="1.8s" repeatCount="indefinite" />
                            </circle>
                            
                            {/* Corner decorative elements */}
                            <circle cx="40" cy="40" r="2" fill="url(#chartGradient1)" opacity="0.4">
                                <animate attributeName="r" values="2;4;2" dur="2s" repeatCount="indefinite" />
                                <animate attributeName="opacity" values="0.4;0.8;0.4" dur="2s" repeatCount="indefinite" />
                            </circle>
                            <circle cx="140" cy="40" r="2.5" fill="url(#chartGradient2)" opacity="0.5">
                                <animate attributeName="r" values="2.5;4.5;2.5" dur="2.2s" begin="0.5s" repeatCount="indefinite" />
                                <animate attributeName="opacity" values="0.5;0.9;0.5" dur="2.2s" begin="0.5s" repeatCount="indefinite" />
                            </circle>
                        </svg>
                    </div>
                    
                    {/* Enhanced Description Text */}
                    <div style={{ textAlign: 'center', maxWidth: '500px' }}>
                        <h2 style={{ 
                            margin: '0 0 15px 0',
                            fontSize: '24px',
                            fontWeight: '700',
                            color: '#2c3e50',
                            textShadow: '0 2px 4px rgba(0,0,0,0.1)',
                            letterSpacing: '0.5px'
                        }}>
                            {t('Reports')}
                        </h2>
                        <p style={{ 
                            margin: '0 0 20px 0',
                            fontSize: '16px',
                            color: '#555',
                            lineHeight: '1.6',
                            fontWeight: '500',
                            maxWidth: '450px',
                            margin: '0 auto 20px auto'
                        }}>
                            {t('SelectReportTypeFirstly')}
                        </p>
                        

                    </div>
                </div>
            )}
            </>
            )}
        </Box>
    </Modal>
    );
};

export default WithholdingModal;
